import { GetSharedCandidatesRosterDataOutput } from '$store/modules/candidates';
import { MutableRefObject, useMemo } from 'react';
import { ReactComponent as Close } from '$icons/CloseX.svg';
import { ReactComponent as WebsiteIcon } from '$icons/uncolored/CountryIcon.svg';
import { ReactComponent as LinkedinIcon } from '$icons/uncolored/linkedin.svg';
import { ReactComponent as BehanceIcon } from '$icons/BehanceIcon.svg';
import { ReactComponent as GithubIcon } from '$icons/GithubIcon.svg';
import DefaultIcon2 from '$components/DefaultIcon2/DefaultIcon2';
import capitalizedName from '$utils/capitalizedName';
import computeYearsOfExperience from '$utils/computeYearsOfExperience';
import { SIMERA_FEE } from '$constants/SimeraOperations';
import SPKAccordion from './SPKAccordion/SPKAccordion';
import SPKSmartScores from './SPKSmartScores/SPKSmartScores';
import SPKCandidateVideos from '../SPKCandidateVideos/SPKCandidateVideos';
import SPKPanelSkills from './SPKPanelSkills/SPKPanelSkills';
import SPKTabs from './SPKTabs/SPKTabs';
import SPKTimeline from './SPKTimeline/SPKTimeline';
import SPKExperienceCard from './SPKExperienceCard/SPKExperienceCard';
import SPKEducationCard from './SPKEducationCard/SPKEducationCard';

const SPKPanel = ({
  candidate,
  parentRef,
  onClose,
  smartScoreInitialStatus = false,
}: {
  candidate:
    | GetSharedCandidatesRosterDataOutput['candidates'][number]
    | undefined;
  parentRef: MutableRefObject<HTMLDivElement | null>;
  onClose: () => void;
  smartScoreInitialStatus?: boolean;
}) => {
  const yearsOfExperience = useMemo(() => {
    return computeYearsOfExperience(candidate?.CandidateWorkExperience);
  }, [candidate]);

  const candidateSmartScores = useMemo(() => {
    if (!candidate || !candidate.AIData) return [];
    const scores = [];

    const highestRoleFit =
      candidate.AIData.job_fits?.reduce((prev, act) => {
        return act.match_score > prev ? act.match_score : prev;
      }, 0) || 0;

    scores.push({
      score: highestRoleFit * 100,
      title: 'Role Fit',
      tooltip:
        "Evaluates how well the candidate aligns with different job categories based on their job interests, work experience, skills, and Simera's market insights using our AI models.",
    });

    // Loyalty
    scores.push({
      title: 'Loyalty',
      score: (candidate.AIData.jump_around?.score || 0) * 100,
      tooltip:
        "Reflects the candidates potential stability and retention based on their job experience when factored against Simera's AI model that accounts for market trends and economic factors.",
    });

    // Communication
    scores.push({
      title: 'Communication',
      score: (candidate.AIData.soft_skills?.communication || 0) * 100,
      tooltip:
        'Using their written and video communications our AI assesses the candidates English proficiency including accent, vocabulary and other skills.',
    });

    // Adaptability
    scores.push({
      title: 'Adaptability',
      score: (candidate.AIData.soft_skills?.adaptability || 0) * 100,
      tooltip:
        'Generated from custom assessments, video communications, work experience and prompting to help identify candidates that are likely to be adaptable to remote environments',
    });

    return scores;
  }, [candidate]);

  const experienceAndEducationTabs = useMemo(() => {
    const tabs = [];
    if (
      candidate?.CandidateWorkExperience &&
      candidate?.CandidateWorkExperience.length > 0
    ) {
      tabs.push({
        title: 'Experience',
        children: (
          <SPKTimeline
            childrens={candidate?.CandidateWorkExperience?.map(
              (singleWorkExp) => (
                <SPKExperienceCard experience={singleWorkExp} />
              )
            )}
          />
        ),
      });
    }

    if (
      candidate?.CandidateEducation &&
      candidate?.CandidateEducation.length > 0
    ) {
      tabs.push({
        title: 'Education',
        children: (
          <SPKTimeline
            childrens={candidate?.CandidateEducation?.map((singleEducation) => (
              <SPKEducationCard education={singleEducation} />
            ))}
          />
        ),
      });
    }

    return tabs;
  }, [candidate?.CandidateEducation, candidate?.CandidateWorkExperience]);

  return (
    <div
      ref={parentRef}
      className="p-4 transform transition-transform duration-500 translate-x-full ease-out w-11/12 lg:w-1/2 h-full bg-spk-offWhite overflow-auto"
    >
      <div className="flex justify-between">
        <div className="text-spk-brightBlue font-semibold text-base">
          Simera Passport Key (SPK)
        </div>
        <button
          type="button"
          className="flex flex-row gap-2 items-center text-black/50 text-sm"
          onClick={onClose}
        >
          Close <Close className="fill-gray-400 w-3 h-3" />
        </button>
      </div>
      <hr className="block my-4" />
      <div className="flex flex-col">
        <div className="rounded-xl bg-white p-4 flex flex-col gap-4 mb-6">
          <div className="flex flex-row gap-4 items-center">
            <div className="w-12">
              <DefaultIcon2
                isEmployer
                firstName={candidate?.User?.FirstName}
                lastName={candidate?.User?.LastName}
                fontSize="text-2xl"
                hideBorder
                picture={candidate?.User?.ProfilePicture?.Url}
              />
            </div>
            <div>
              <div className="flex font-bold text-base mb-1">
                {`${capitalizedName(
                  candidate?.User?.FirstName
                )} ${capitalizedName(candidate?.User?.LastName)}.`}
              </div>
              <div className="text-sm">
                <div className="flex flex-row divide-x divide-gray-400 *:px-2 -mx-2">
                  {candidate?.CandidateWorkExperience?.[0]?.JobTitle && (
                    <span>
                      {candidate?.CandidateWorkExperience?.[0]?.JobTitle}
                    </span>
                  )}
                  {candidate?.CandidateJobInterest?.SalaryUpperBound && (
                    <span>
                      $
                      {(candidate?.CandidateJobInterest?.SalaryUpperBound ||
                        0) + SIMERA_FEE}
                      /m USD
                    </span>
                  )}
                  <span>{yearsOfExperience}+ yrs exp</span>
                </div>
              </div>
            </div>
          </div>
          <div className="flex flex-row divide-x-2 divide-neutral-100 *:px-2 -mx-2">
            {candidate?.AdditionalVideos && (
              <div className="w-1/2">
                <SPKCandidateVideos
                  videos={candidate?.AdditionalVideos}
                  maxH="max-h-60"
                />
              </div>
            )}
            <div className="w-1/2 flex flex-col">
              {candidate?.WorkSummary && (
                <span className="mb-4">{candidate?.WorkSummary}</span>
              )}
              {candidate?.CandidateSkills && (
                <SPKPanelSkills skills={candidate?.CandidateSkills} />
              )}

              {/* Portafolios and websites HIDDEN TEMP */}
              <div className="mt-auto flex-row gap-4 hidden">
                {candidate?.CustomUrl && (
                  <button
                    type="button"
                    className="flex items-center bg-spk-brightBlue/20 p-2 rounded-full"
                    title="Website"
                  >
                    <WebsiteIcon className="fill-spk-brightBlue w-4 h-4" />
                  </button>
                )}
                {candidate?.LinkedinUrl && (
                  <button
                    type="button"
                    className="flex items-center bg-spk-brightBlue/20 p-2 rounded-full"
                    title="Linkedin"
                  >
                    <LinkedinIcon className="fill-spk-brightBlue w-4 h-4" />
                  </button>
                )}
                {candidate?.BehanceUrl && (
                  <button
                    type="button"
                    className="flex items-center bg-spk-brightBlue/20 p-2 rounded-full"
                    title="Behance"
                  >
                    <BehanceIcon className="fill-spk-brightBlue w-4 h-4" />
                  </button>
                )}
                {candidate?.GithubUrl && (
                  <button
                    type="button"
                    className="flex items-center bg-spk-brightBlue/20 p-2 rounded-full"
                    title="Github"
                  >
                    <GithubIcon className="fill-spk-brightBlue w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="gap-2 flex flex-col">
          {candidateSmartScores &&
            candidate?.AIData?.job_fits &&
            candidate?.AIData?.soft_skills &&
            candidateSmartScores.length > 0 && (
              <SPKAccordion
                title="Smart Scores"
                isOpen={smartScoreInitialStatus}
              >
                <SPKSmartScores
                  scores={candidateSmartScores}
                  jobFits={candidate?.AIData?.job_fits}
                  softSkills={candidate?.AIData?.soft_skills}
                />
              </SPKAccordion>
            )}
          <SPKAccordion title="Experience">
            <SPKTabs tabs={experienceAndEducationTabs} />
          </SPKAccordion>
        </div>
      </div>
    </div>
  );
};

SPKPanel.defaultProps = {
  smartScoreInitialStatus: false,
};

export default SPKPanel;
